import React, { useState, useEffect, useRef } from 'react'
import { Table } from '@mantine/core'

/**
 * What the search shows and navigates by. A whole entity has these, and so
 * does a row of the Entity Management grid, which the grid's search reads.
 */
export type EntitySearchItem = {
    id?: string
    status?: string
    entityName?: string
    mei?: string | null
    internalId?: string | null
    systemId?: string | null
}

interface Props {
    data: EntitySearchItem[]
    searchValue: string
    onEntitySelect?: (entity: EntitySearchItem) => void
    onClose?: () => void
}

export function EntitySearchTable({
    data,
    searchValue,
    onEntitySelect,
    onClose,
}: Props) {
    const [selectedIndex, setSelectedIndex] = useState(0)
    const selectedRowRef = useRef<HTMLTableRowElement>(null)

    useEffect(() => {
        // Reset to first item when data changes
        setSelectedIndex(0)
    }, [data])

    useEffect(() => {
        // Scroll selected row into view
        if (selectedRowRef.current) {
            selectedRowRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            })
        }
    }, [selectedIndex])

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (data.length === 0) return

            switch (e.key) {
                case 'Enter':
                    e.preventDefault()
                    if (selectedIndex >= 0 && selectedIndex < data.length) {
                        onEntitySelect?.(data[selectedIndex])
                    }
                    break
                case 'ArrowDown':
                    e.preventDefault()
                    setSelectedIndex(prev => Math.min(prev + 1, data.length - 1))
                    break
                case 'ArrowUp':
                    e.preventDefault()
                    setSelectedIndex(prev => Math.max(prev - 1, 0))
                    break
                case 'Escape':
                    e.preventDefault()
                    onClose?.()
                    break
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [data, selectedIndex, onEntitySelect, onClose])

    const escapeRegExp = (string: string) => {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    }

    const highlightText = (text: string) => {
        if (!searchValue || !text) return text

        const escapedSearch = escapeRegExp(searchValue)
        const parts = text.split(new RegExp(`(${escapedSearch})`, 'gi'))
        return parts.map((part, index) =>
            part.toLowerCase() === searchValue.toLowerCase() ? (
                <span key={index} className="entitySearchResult">
                    {part}
                </span>
            ) : (
                part
            ),
        )
    }

    if (data.length === 0) {
        return (
            <div className="searchMessageText">
                {searchValue.trim().length < 2
                    ? 'Type at least 2 characters to search...'
                    : 'No entities found matching your search.'}
            </div>
        )
    }

    return (
        <div className="entityTableContainer">
            <div className="entityTableScroll">
                <Table className="entityTable" highlightOnHover>
                    <thead>
                        <tr>
                            <th>Entity Name</th>
                            <th>MEI</th>
                            <th>Internal Entity ID</th>
                            <th>Entity ID</th>
                        </tr>
                    </thead>

                    <tbody>
                        {data.map((entity, index) => (
                            <tr
                                key={entity.id}
                                ref={selectedIndex === index ? selectedRowRef : null}
                                onClick={() => {
                                    onEntitySelect?.(entity)
                                }}
                                style={{
                                    cursor: 'pointer',
                                    backgroundColor:
                                        selectedIndex === index ? '#e3e7ff' : undefined,
                                }}
                            >
                                <td>{highlightText(entity.entityName || 'N/A')}</td>
                                <td>{highlightText(entity.mei || 'N/A')}</td>
                                <td>{highlightText(entity.internalId || 'N/A')}</td>
                                <td>{highlightText(entity.systemId || 'N/A')}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
        </div>
    )
}
